/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { Grid } from '@mui/system';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { isEqual, isNil } from 'lodash';
import { CustomPrimaryUser, UserPermission } from 'api';
import { firmsAction, globalAction } from 'common/actions';
import {
  ADMIN,
  COMPANY,
  COMPANY_USER_VALUE,
  EXISTING_VALID_ROLES_VALUES,
  FIRM,
  FUND,
  FUND_ADMIN_VALUE,
  PERMISSIONS_NOT_SAVED_ERROR,
  PERMISSIONS_SAVED,
} from 'common/constants/user';
import { useStore } from 'common/store';
import { CompanyByFirm } from 'common/types/company';
import { Firm } from 'common/types/firm';
import { UseStoreValues } from 'common/types/store';
import { User } from 'common/types/user';
import { MessageBox } from 'components';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { LayoutContext } from 'context';
import {
  BaseItem,
  ItemType,
  Permission,
  SelectedRole,
  UserDataWithFirmPermission,
} from 'pages/UserPermissions/common/types';
import { UsersService } from 'services';
import { useResponse } from 'services/hooks';
import { useAddUserToFirm, useSaveUserPermissions } from 'services/hooks/useManageUser/useManageUser';
import { useGetUserDetails, useGetUserPermissions } from 'services/hooks/useUserSelected';
import { Logger } from 'utilities';
import CompanyFundSelect from './components/CompanyFundSelect';
import FirmItems from './components/FirmItems';
import UserCard from './components/UserCard';
import UserDirectory from './components/UserDirectory';
import {
  StyledFormLabel,
  StyledGridContainer,
  StyledGridContainerSubtitle,
  StyledGridRoot,
  StyledGridSection,
  StyledGridSectionBorderLeft,
  StyledPerfectScrollbar,
  StyledScrollbarContainer,
  StyledTypography,
} from './UserPermissions.styles';
import { userHasAccessType } from './utils';
import { getAllPotentialPermissions } from './utils/handlePermissions';
import { UserPermissions as UserPermissionsType } from './utils/handlePermissions/handlePermissions';
import { prettyLastLogin, prettyRoleType, prettyUsername } from './utils/prettyUserData';
import translateRoleIntoPermissions, { UserRole } from './utils/translateRoleIntoPermissions';

const UserPermissions = () => {
  const { updatePageActions } = useContext(LayoutContext);
  const [
    {
      userManagementFirmList,
      companyList,
      firmList,
      fundList,
      firmInfo,
      user, // Logged in user
      selectedUserPermissions,
      isShowLoadingProgress,
    },
    dispatch,
  ] = useStore() as unknown as UseStoreValues;

  const { successNotification, errorNotification } = useResponse();
  const [currentUser, setCurrentUser] = useState<CustomPrimaryUser>();
  const [selectedFirm, setSelectedFirm] = useState<Firm | null>();
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [originalUserPermissions, setOriginalUserPermissions] = useState<UserPermission[]>([]);
  const [userPermissionsToSave, setUserPermissionsToSave] = useState<UserPermissionsType | null>(null);
  const [allPotentialPermissions, setAllPotentialPermissions] = useState<Permission[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRole, setSelectedRole] = useState<SelectedRole | null>();
  const [firmItem, setFirmItem] = useState<ItemType>({
    itemType: null,
    itemId: null,
  });
  const [isSaving, setIsSaving] = useState(false);

  // Custom hooks
  const { data: userSelected } = useGetUserDetails((currentUser?.id as number) ?? user?.id);
  const { data: userPermissions } = useGetUserPermissions(
    (currentUser?.id as number) ?? user?.id,
    selectedFirm?.id as number
  );
  const { mutate: addUserToFirm } = useAddUserToFirm();
  const { mutate: savePermissions, updatePermissionsForCompOrFundRole } = useSaveUserPermissions();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (userPermissions && !selectedRole) {
      setUserPermissionsToSave({
        user_id: userSelected?.id as number,
        permissions: userPermissions,
      });
    }

    return () => {
      setUserPermissionsToSave(null);
    };
  }, [userPermissions, userSelected, selectedRole]);

  useEffect(() => {
    if (userPermissions) {
      setOriginalUserPermissions(userPermissions);
    }
  }, [userPermissions]);

  const handleAddUserToFirm = useCallback(
    async (userData: User, role: UserRole, handleDialogClose: () => void) => {
      if (selectedFirm?.id && fundList && companyList) {
        const roleToPermissions = translateRoleIntoPermissions({
          role,
          firmId: selectedFirm?.id,
          companyList: companyList as unknown as CompanyByFirm[],
          fundList,
        });

        if (roleToPermissions && roleToPermissions.length > 0) {
          addUserToFirm(
            {
              firmId: selectedFirm?.id,
              userData,
              permissions: roleToPermissions,
            },
            {
              onSuccess: () => {
                handleDialogClose();
                successNotification('User invited successfully');
              },
              onError: response => {
                errorNotification(JSON.parse(response.response.text).User[0]);
              },
            }
          );
        }
      }
    },
    [selectedFirm, companyList, fundList, addUserToFirm]
  );

  const userFullNameOrEmail = useMemo(() => prettyUsername(userSelected), [userSelected]);

  const isFirmAdmin = useMemo(() => {
    if (!isNil(firmList) && !isNil(userManagementFirmList) && !isNil(selectedFirm)) {
      const matchingFirm = firmList.find(firm => firm.id === selectedFirm?.id);
      if (matchingFirm) {
        const currentFirmPermissions = matchingFirm?.user_permissions?.map(permission => ({
          [permission.user_id as string]: permission.access_type,
        }));

        if (currentFirmPermissions && user?.id) {
          return isEqual(currentFirmPermissions, [{ [user?.id.toString()]: ADMIN }]);
        }

        return false;
      }

      return false;
    }

    return false;
  }, [selectedFirm, firmList, userManagementFirmList]);

  // Currently logged-in user is (1) superuser or (2) a firm admin
  const isSuperUserOrFirmAdmin = useMemo(() => (isSuperUser || isFirmAdmin) ?? false, [isFirmAdmin, isSuperUser]);

  const setSelectedFirmAndFetchUserPermissions = (value: Firm) => {
    if (!value) {
      setSelectedFirm(null);
    } else {
      setSelectedFirm({
        ...value,
        user_permissions: selectedUserPermissions?.find(item => item.id === value?.id)?.user_permissions ?? [],
      });
    }
  };

  useEffect(() => {
    if (firmInfo) {
      setSelectedFirmAndFetchUserPermissions(firmInfo);
    }
  }, [firmInfo]);

  useEffect(() => {
    const getUserInfo = async () => {
      if (user?.id) {
        const usersService = new UsersService();
        const userInfo = await usersService.getUserProfileById(user.id);
        if (userInfo) {
          const { is_superuser: userIsSuper, firms_permissions: firmPermissions } = userInfo;
          setIsSuperUser(userIsSuper);
          // Populate the global store with the firms available to the selected user
          dispatch(firmsAction.setUserManagementFirmList(firmPermissions));
        }
      }
    };
    if (user?.id) {
      // Permissions for the logged in user
      getUserInfo();
    }
  }, [user]);

  useEffect(() => {
    if (userManagementFirmList?.length === 1) {
      setSelectedFirm(userManagementFirmList[0]);
    }
  }, [userManagementFirmList]);

  const lastLogin = useMemo(() => prettyLastLogin(userSelected), [userSelected]);

  // Implicitly check all permissions if the user is a superuser or a firm admin.
  // These are the only types of accounts that can grant permissions to a user.
  const shouldCheckAll = useMemo(() => {
    if (userSelected?.id && selectedFirm?.id) {
      return userHasAccessType({
        permissions: userPermissions,
        accessType: ADMIN,
        userId: userSelected?.id,
        objectId: selectedFirm.id,
        objectType: FIRM,
      });
    }
    return false;
  }, [userPermissions, userSelected, selectedFirm]);

  /* Re-enable when the individual permissions approach is implemented
  const hasEditPermissionsOnAllFundsAndComps = useMemo(() => {
    if (companyList && fundList && userPermissionsToSave) {
      return userCanEditAllFundsAndComps({
        companyList: companyList as unknown as CompanyByFirm[],
        fundList,
        userPermissionsToSave,
      });
    }

    return false;
  }, [companyList, fundList, userPermissionsToSave]);
  */

  const roleType = useMemo(() => {
    if (userSelected?.id && selectedFirm?.id) {
      const userDataWithFirmPermission = {
        ...userSelected,
        firms_permissions: userSelected?.firms_permissions ?? [],
      } as UserDataWithFirmPermission;
      return prettyRoleType(selectedRole?.roleType ?? null, userDataWithFirmPermission, selectedFirm.id);
    }
    return null;
  }, [selectedRole, userSelected, selectedFirm]);

  const handleSave = useCallback(async () => {
    if (isSaving) {
      return;
    }

    const isValidRoleType = roleType !== null && roleType !== undefined;
    if (userSelected?.id && selectedFirm?.id && firmInfo && isValidRoleType) {
      // Set saving state immediately to disable button
      setIsSaving(true);
      dispatch(globalAction.showLoadingProgress(true));

      savePermissions(
        {
          userId: userSelected?.id,
          roleType,
          firmId: selectedFirm?.id,
          userPermissionsToSave,
        },
        {
          onSuccess: data => {
            setOriginalUserPermissions(data);
            successNotification(PERMISSIONS_SAVED);
            setSelectedFirmAndFetchUserPermissions(firmInfo);
            // Invalidate queries to refresh user details and permissions
            if (userSelected?.id) {
              queryClient.invalidateQueries({ queryKey: ['userDetails', userSelected.id] });
              queryClient.invalidateQueries({ queryKey: ['userPermissions', userSelected.id, selectedFirm.id] });
            }
            setIsSaving(false);
            dispatch(globalAction.showLoadingProgress(false));
          },
          onError: error => {
            errorNotification(PERMISSIONS_NOT_SAVED_ERROR);
            setIsSaving(false);
            dispatch(globalAction.showLoadingProgress(false));
            Logger.error('Error saving user permissions', error);
          },
        }
      );
    }
  }, [userSelected, userPermissionsToSave, roleType, queryClient, isSaving]);

  useEffect(() => {
    if (updatePageActions) {
      updatePageActions({
        mainAction: {
          id: 'user-permissions',
          label: 'Save',
          // Disable the Save button if the user has not changed any permissions.
          // Also, all permissions must have a valid object_id. This is important
          // specially for company users and fund admins that must be associated
          // with a given company or fund.
          // Note: the isActive is inverted here based on the mainAction as used
          // in ActionButtons. It should be enabled if false and disabled otherwise.
          isActive:
            !isSaving &&
            isEqual(userPermissionsToSave?.permissions, originalUserPermissions) &&
            userPermissionsToSave?.permissions?.every(perm => perm.feature_object?.object_id),
          callback: handleSave,
        },
      });
    }

    return () => {
      if (updatePageActions) {
        updatePageActions(null);
      }
    };
  }, [userPermissionsToSave, originalUserPermissions, updatePageActions, isSaving, handleSave]);

  useEffect(() => {
    if (selectedFirm && selectedUserPermissions) {
      dispatch(
        firmsAction.setSelectedUserPermissions([
          ...selectedUserPermissions.filter(firm => firm.id !== selectedFirm.id),
          selectedFirm,
        ])
      );
    }
  }, [selectedFirm]);

  // Combine all funds and companies as potential permissions on the selected firm
  useEffect(() => {
    if (selectedFirm && fundList && companyList && userPermissionsToSave) {
      const allMissingFundsAndCompsPermissions = getAllPotentialPermissions({
        userPermissionsToSave,
        fundList,
        companyList: companyList as unknown as CompanyByFirm[],
      });
      setSelectAll(allMissingFundsAndCompsPermissions.length === 0);
      setAllPotentialPermissions(allMissingFundsAndCompsPermissions);
    }
  }, [selectedFirm, fundList, companyList, userPermissionsToSave]);

  const handleSelect = ({ itemType, itemId }: ItemType) => {
    if (![COMPANY, FUND].includes(itemType as string) || !roleType) {
      return;
    }

    setFirmItem({ itemType, itemId });

    setUserPermissionsToSave(prevPermissionsToSave =>
      updatePermissionsForCompOrFundRole(
        itemType as string,
        itemId as number,
        prevPermissionsToSave,
        roleType,
        selectedFirm,
        userSelected
      )
    );
  };

  /** This function is to obtain the value as it is in the 'value' prop of the select this is necessary because until you change for the first time the selected company
   * or fund the permissions are not updated.
   * Therefore, we need to replicate the process used in the 'handleSelect' function to update the permissions.
   */
  const getSelectInitialValue = (itemType: string) => {
    const itemId = userPermissions?.find(perm => perm?.feature_object?.object_type === itemType)?.feature_object
      ?.object_id;
    return itemId ? { initialItemType: itemType, initialItemId: itemId } : null;
  };

  const getCompanyOrFundSelectInitialValue = () => {
    if (roleType === COMPANY_USER_VALUE) {
      return getSelectInitialValue(COMPANY);
    }
    if (roleType === FUND_ADMIN_VALUE) {
      return getSelectInitialValue(FUND);
    }
    return null;
  };

  useEffect(() => {
    const selectedRoleType = selectedRole?.roleType;
    const isValidRoleType = selectedRoleType !== null && selectedRoleType !== undefined;
    if (companyList && fundList && isValidRoleType && EXISTING_VALID_ROLES_VALUES.includes(selectedRoleType)) {
      const roleToPermissions = translateRoleIntoPermissions({
        role: { id: selectedRoleType, firmItem },
        firmId: selectedFirm?.id as number,
        companyList: companyList as unknown as CompanyByFirm[],
        fundList,
      });

      const permissionsUpdated: Permission[] = (userPermissions ?? [])
        .filter(perm => perm.id !== 0)
        .map(perm => ({ ...perm, remove_permission: true }));

      setUserPermissionsToSave(prevPermissionsToSave => ({
        ...prevPermissionsToSave,
        permissions: [...permissionsUpdated, ...(roleToPermissions ?? [])],
        user_id: userSelected?.id,
        role_type: selectedRoleType,
      }));
    }
    // These conditions avoid cases where the firmItem already was set and the roletype is not FUND_ADMIN_VALUE or COMPANY_USER_VALUE
    if (!firmItem.itemType && !firmItem.itemId && (roleType === FUND_ADMIN_VALUE || roleType === COMPANY_USER_VALUE)) {
      const initialFirmItem = getCompanyOrFundSelectInitialValue();
      if (initialFirmItem) {
        const { initialItemType, initialItemId } = initialFirmItem;
        if (initialItemType && initialItemId) {
          setUserPermissionsToSave(prevPermissionsToSave =>
            updatePermissionsForCompOrFundRole(
              initialItemType,
              initialItemId,
              prevPermissionsToSave,
              roleType,
              selectedFirm,
              userSelected
            )
          );
        }
      }
    }
  }, [selectedRole, selectedFirm, companyList, fundList, firmItem, userPermissions, userSelected]);

  useEffect(() => setSelectedRole(null), [currentUser]);

  const handleCheckAll = useCallback(
    event => {
      const { checked } = event.target;
      setSelectAll(checked);
      if (checked) {
        const permissionsToAdd = allPotentialPermissions.filter(
          item =>
            !userPermissionsToSave?.permissions?.find(
              perm =>
                perm?.feature_object?.object_id === item?.feature_object?.object_id &&
                perm?.feature_object?.object_type === item?.feature_object?.object_type
            )
        );

        setUserPermissionsToSave(prevPermissionsToSave => ({
          ...prevPermissionsToSave,
          permissions: [
            ...(prevPermissionsToSave?.permissions ?? []).map(perm => ({ ...perm, remove_permission: false })),
            ...permissionsToAdd,
          ],
        }));
      } else {
        setUserPermissionsToSave({
          ...userPermissionsToSave,
          permissions: (userPermissionsToSave?.permissions || [])
            .filter(perm => perm.id !== 0)
            .map(perm => ({ ...perm, remove_permission: true })),
        });
      }
    },
    [allPotentialPermissions]
  );

  if (companyList && fundList && !isSuperUserOrFirmAdmin) {
    return (
      <MessageBox
        title="You do not have permissions to view this page"
        tagline="Please contact your supervisor if you believe this is an error"
        fullWidth={false}
      />
    );
  }

  if (isShowLoadingProgress) {
    return <LoadingIndicator isLoading />;
  }

  if (companyList && fundList && isSuperUserOrFirmAdmin) {
    return (
      <StyledGridRoot container>
        <StyledGridSection size={{ sm: 6 }}>
          {selectedFirm && (
            <>
              <Typography variant="h6">User Management Detail View</Typography>
              <br />
              <UserCard
                name={userFullNameOrEmail}
                lastLogin={lastLogin}
                email={userSelected?.email ?? ''}
                pictureUrl={userSelected?.profile?.picture_url ?? ''}
                roleType={roleType as number}
                setSelectedRole={setSelectedRole}
                disabled={userSelected?.id === user?.id}
              />
              <StyledGridContainer>
                {[COMPANY_USER_VALUE, FUND_ADMIN_VALUE].includes(roleType as number) && (
                  <StyledFormLabel>{`Select ${roleType === COMPANY_USER_VALUE ? COMPANY : FUND}`}</StyledFormLabel>
                )}
                {roleType === FUND_ADMIN_VALUE && (
                  <CompanyFundSelect
                    value={
                      firmItem.itemId ??
                      userPermissions?.find(perm => perm?.feature_object?.object_type === FUND)?.feature_object
                        ?.object_id
                    }
                    handleSelect={handleSelect}
                    itemType={FUND}
                    itemList={
                      (fundList?.map(fund => ({
                        id: fund.id,
                        name: fund.name,
                      })) as BaseItem[]) ?? []
                    }
                  />
                )}
                {roleType === COMPANY_USER_VALUE && (
                  <CompanyFundSelect
                    value={
                      firmItem.itemId ??
                      userPermissions?.find(perm => perm?.feature_object?.object_type === COMPANY)?.feature_object
                        ?.object_id
                    }
                    handleSelect={handleSelect}
                    itemType={COMPANY}
                    itemList={
                      (companyList?.map(company => ({
                        id: company.company_id,
                        name: company.company_name,
                      })) as BaseItem[]) ?? []
                    }
                  />
                )}
              </StyledGridContainer>
              <StyledGridContainerSubtitle container spacing={1}>
                {/* Re-enable when the individual permissions approach is implemented
                <StyledFormControlLabel
                  control={
                    <Checkbox
                      onChange={handleCheckAll}
                      color="primary"
                      checked={shouldCheckAll || selectAll || hasEditPermissionsOnAllFundsAndComps}
                      indeterminate={
                        !selectAll
                        && !hasEditPermissionsOnAllFundsAndComps
                        && !shouldCheckAll
                        && userPermissionsToSave?.permissions?.some(perm => !perm.remove_permission)
                      }
                      disabled
                    />
                  }
                  label="Select all funds and companies"
                  labelPlacement="end"
                />
                */}
                <Grid size={{ sm: 12 }}>
                  <StyledPerfectScrollbar
                    className={clsx('always-visible')}
                    options={{ suppressScrollX: true }}
                    id="scrollbar-firm-items">
                    <StyledScrollbarContainer>
                      <Grid container spacing={1}>
                        <Grid size={{ sm: 8 }}>
                          <StyledTypography style={{ fontSize: '1.2rem' }} id="fund-subtitle">
                            Funds
                          </StyledTypography>
                        </Grid>
                        <Grid size={{ sm: 2 }}>
                          <Typography variant="inherit">Edit</Typography>
                        </Grid>
                        <Grid size={{ sm: 2 }}>
                          <Typography variant="inherit">View</Typography>
                        </Grid>
                        <FirmItems
                          itemType="Fund"
                          firmItems={(fundList?.map(({ id, name }) => ({ id, name })) as BaseItem[]) ?? []}
                          userPermissions={userPermissionsToSave?.permissions ?? []}
                          // If the currently selected account is the logged-in user and it has edit access or higher,
                          // then he/she has implicit permissions over all the companies and funds in the firm.
                          shouldCheckAll={shouldCheckAll || selectAll}
                          setUserPermissionsToSave={setUserPermissionsToSave}
                          disabled
                        />
                        <Grid size={{ sm: 12 }}>
                          <StyledTypography style={{ fontSize: '1.2rem' }} id="company-subtitle">
                            Companies
                          </StyledTypography>
                        </Grid>
                        <FirmItems
                          itemType="Company"
                          firmItems={
                            (companyList?.map(({ company_id: id, company_name: name }) => ({
                              id,
                              name,
                            })) as unknown as BaseItem[]) ?? []
                          }
                          userPermissions={userPermissionsToSave?.permissions ?? []}
                          shouldCheckAll={shouldCheckAll || selectAll}
                          setUserPermissionsToSave={setUserPermissionsToSave}
                          disabled
                        />
                      </Grid>
                    </StyledScrollbarContainer>
                  </StyledPerfectScrollbar>
                </Grid>
              </StyledGridContainerSubtitle>
            </>
          )}
        </StyledGridSection>
        <StyledGridSectionBorderLeft size={{ sm: 6 }}>
          <UserDirectory
            setCurrentUser={setCurrentUser}
            selectedFirm={selectedFirm as BaseItem}
            isFirmAdmin={isFirmAdmin}
            companyList={companyList}
            fundList={fundList as BaseItem[]}
            isSuperUser={isSuperUser}
            handleAddUser={handleAddUserToFirm}
            currentUser={currentUser}
          />
        </StyledGridSectionBorderLeft>
      </StyledGridRoot>
    );
  }

  return <LoadingIndicator isLoading={!fundList && !companyList} />;
};

export default UserPermissions;
